// Puts that are patches, laid over what an entry has rather than replacing it: a sheet's band changed by its cells
// (shared/sheet/model.ts), and a slide changed by its fields, boxes and places (shared/slideDesign.ts, S5). So a user
// and an agent, or two people, changing other parts of one entry at once both keep theirs.

import { applyPatch, isCellsPatch, joinPatches, type CellsData, type CellsPatch } from "../../shared/sheet/model";
import { applySlidePatch, type SlideData } from "../../shared/slides";
import { isSlidePatch, joinSlidePatches } from "../../shared/slideDesign";
import type { Entry } from "../../shared/types";

/** Whether an entry's put is a patch, to lay over the entry rather than replace it. */
export function isPatch(e: Pick<Entry, "t" | "data">): boolean {
  return isCellsPatch(e) || isSlidePatch(e);
}

/** A put's data laid over what the entry had (null: the entry is not here yet). */
export function layData(was: Record<string, unknown> | null, e: Entry): Record<string, unknown> {
  if (isCellsPatch(e)) return applyPatch(was as unknown as CellsData | null, e.data as unknown as CellsPatch) as unknown as Record<string, unknown>;
  if (isSlidePatch(e)) {
    try {
      return applySlidePatch(was as unknown as SlideData | null, e.data) as unknown as Record<string, unknown>;
    } catch {
      return was ?? {};
    }
  }
  return e.data;
}

/** An entry with a put laid over it (or the put's entry, when it is whole). */
export function layEntry(was: Entry | null | undefined, e: Entry): Entry {
  return isPatch(e) ? { ...e, data: layData(was?.data ?? null, e) } : e;
}

/** Two puts to one entry waiting to go, as one: patches joined, a patch over a whole entry laid on it. */
export function joinPuts(older: Entry, newer: Entry): Entry {
  if (!isPatch(newer)) return newer;
  if (isCellsPatch(newer)) {
    if (isCellsPatch(older)) return joinPatches(older, newer);
    if (older.t !== "cells") return newer;
    return { ...newer, data: layData(older.data, newer) };
  }
  if (isSlidePatch(older)) return joinSlidePatches(older, newer);
  if (older.t !== "slide") return newer;
  return { ...newer, data: layData(older.data, newer) };
}

/**
 * A step's change to a slide taken back (or put forward again) over the slide as it is now (S5): its fields as any
 * entry's (shiftData); its boxes by id and, within a box, only the properties the step changed, so a box another
 * writer added since stays, and words typed in a box since a move stay when the move is undone; its fields' places
 * field by field, likewise.
 */
export function shiftSlide(cur: Entry, from: Entry, to: Entry, shiftData: (cur: Entry, from: Entry, to: Entry) => Entry): Entry {
  const strip = (e: Entry): Entry => {
    const { place: _p, boxes: _b, ...data } = e.data;
    return { ...e, data };
  };
  const c = strip(cur);
  const base = shiftData(c, strip(from), strip(to));
  const boxes = shiftByKey(byId(cur.data.boxes), byId(from.data.boxes), byId(to.data.boxes));
  const place = shiftByKey(asMap(cur.data.place), asMap(from.data.place), asMap(to.data.place));
  if (base === c && !boxes.hit && !place.hit) return cur;
  const list = Object.values(boxes.out).sort((a, b) => Number(a.z) - Number(b.z) || (String(a.id) < String(b.id) ? -1 : 1));
  const data: Record<string, unknown> = { ...base.data, ...(list.length ? { boxes: list } : {}), ...(Object.keys(place.out).length ? { place: place.out } : {}) };
  return { ...base, data };
}

type Obj = Record<string, unknown>;
const asMap = (v: unknown): Record<string, Obj> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, Obj>) : {});
const byId = (v: unknown): Record<string, Obj> => Object.fromEntries((Array.isArray(v) ? (v as Obj[]) : []).filter((b) => b && typeof b.id === "string").map((b) => [b.id as string, b]));
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Things by key moved from one state to another as they are now, and within each only the properties that changed. */
function shiftByKey(cur: Record<string, Obj>, from: Record<string, Obj>, to: Record<string, Obj>): { out: Record<string, Obj>; hit: boolean } {
  const out: Record<string, Obj> = { ...cur };
  let hit = false;
  for (const k of new Set([...Object.keys(from), ...Object.keys(to)])) {
    const f = from[k];
    const t = to[k];
    if (same(f, t)) continue;
    if (!t) {
      // the step made it: it goes
      if (out[k]) {
        delete out[k];
        hit = true;
      }
    } else if (!f) {
      // the step removed it: it comes back, unless it is here again
      if (!out[k]) {
        out[k] = t;
        hit = true;
      }
    } else if (out[k]) {
      const next: Obj = { ...out[k] };
      for (const p of new Set([...Object.keys(f), ...Object.keys(t)])) {
        if (same(f[p], t[p]) || same(next[p], t[p])) continue;
        if (t[p] === undefined) delete next[p];
        else next[p] = t[p];
        hit = true;
      }
      out[k] = next;
    }
  }
  return { out, hit };
}
